import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { CollegeAccount, normalizeAccount } from '../types';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

type TypeFilter = 'all' | 'college' | 'alumni';
type StatusFilter = 'all' | 'active' | 'inactive';

export function DashboardPage() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<CollegeAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activatedTotal, setActivatedTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const navigate = useNavigate();

  // Only query Firestore once admin status is confirmed (user is set by AuthContext
  // only after the isAdmin check passes). Depending on `user` means this also
  // re-fires correctly if the user re-authenticates within the same session.
  useEffect(() => {
    if (!user) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [accountsSnap, studentsSnap] = await Promise.all([
        getDocs(query(collection(db, 'collegeAccounts'), orderBy('name'))),
        getDocs(collection(db, 'collegeStudents')),
      ]);

      const data = accountsSnap.docs.map((d) => normalizeAccount(d.id, d.data()));
      setAccounts(data);

      const activated = studentsSnap.docs.filter((d) => d.data().activated === true).length;
      setActivatedTotal(activated);
    } catch (err: any) {
      setError(err?.message || 'Failed to load accounts.');
    } finally {
      setLoading(false);
    }
  };

  const filtered = accounts.filter((a) => {
    if (typeFilter !== 'all' && a.type !== typeFilter) return false;
    if (statusFilter === 'active' && !a.active) return false;
    if (statusFilter === 'inactive' && a.active) return false;
    return true;
  });

  const totalColleges = accounts.filter((a) => a.type === 'college').length;
  const totalAlumni = accounts.filter((a) => a.type === 'alumni').length;
  const totalActive = accounts.filter((a) => a.active).length;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Layout>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
          marginBottom: 36,
        }}
      >
        <div className="stat-card">
          <div className="stat-label">Total Colleges</div>
          <div className="stat-value">{loading ? '—' : totalColleges}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Alumni Associations</div>
          <div className="stat-value">{loading ? '—' : totalAlumni}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active Colleges</div>
          <div className="stat-value" style={{ color: 'var(--accent-text)' }}>
            {loading ? '—' : totalActive}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Activated</div>
          <div className="stat-value">{loading ? '—' : activatedTotal}</div>
        </div>
      </div>

      <div className="page-header">
        <h1 className="page-title">Colleges</h1>
        <button className="btn-primary" onClick={() => navigate('/accounts/new')}>
          + Add New College
        </button>
      </div>

      <div className="filters">
        {(['all', 'college', 'alumni'] as TypeFilter[]).map((f) => (
          <button
            key={f}
            className={`filter-btn ${typeFilter === f ? 'active' : ''}`}
            onClick={() => setTypeFilter(f)}
          >
            {f === 'all' ? 'All Types' : f === 'college' ? 'College' : 'Alumni'}
          </button>
        ))}
        <div style={{ width: 1, height: 24, background: 'var(--border)', margin: '0 4px' }} />
        {(['all', 'active', 'inactive'] as StatusFilter[]).map((f) => (
          <button
            key={f}
            className={`filter-btn ${statusFilter === f ? 'active' : ''}`}
            onClick={() => setStatusFilter(f)}
          >
            {f === 'all' ? 'All Status' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading">Loading accounts...</div>
        ) : error ? (
          <div style={{ padding: 24 }}>
            <div className="error-message">{error}</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-secondary)' }}>
            No accounts found
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Plan</th>
                  <th>Students / Members</th>
                  <th>Activation</th>
                  <th>Status</th>
                  <th>Expiry</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((account) => (
                  <tr key={account.id}>
                    <td>
                      <Link
                        to={`/accounts/${account.id}`}
                        style={{ fontWeight: 500, color: 'var(--text-primary)' }}
                      >
                        {account.name}
                      </Link>
                      {account.city && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                          {account.city}, {account.state}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${account.type}`}>
                        {account.type === 'college' ? 'College' : 'Alumni'}
                      </span>
                    </td>
                    <td style={{ textTransform: 'capitalize', color: 'var(--text-secondary)' }}>
                      {account.plan}
                    </td>
                    <td>{account.studentLimit?.toLocaleString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {account.domainEnabled && (
                          <span className="badge" style={{ background: 'rgba(0,232,122,0.12)', color: 'var(--accent-text)' }}>
                            Domain
                          </span>
                        )}
                        {account.csvEnabled && (
                          <span className="badge" style={{ background: 'var(--info-dim)', color: 'var(--info)' }}>
                            CSV
                          </span>
                        )}
                        {!account.domainEnabled && !account.csvEnabled && (
                          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`badge badge-${account.active ? 'active' : 'inactive'}`}>
                        {account.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td
                      style={{
                        color: isExpiringSoon(account.expiryDate)
                          ? 'var(--warning)'
                          : 'var(--text-secondary)',
                        fontSize: 13,
                      }}
                    >
                      {formatDate(account.expiryDate)}
                    </td>
                    <td>
                      <button
                        className="btn-secondary"
                        onClick={() => navigate(`/accounts/${account.id}`)}
                        style={{ padding: '5px 14px', fontSize: 13 }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}

function isExpiringSoon(dateStr: string): boolean {
  if (!dateStr) return false;
  const expiry = new Date(dateStr);
  const now = new Date();
  const in30Days = new Date();
  in30Days.setDate(now.getDate() + 30);
  return expiry >= now && expiry <= in30Days;
}
